import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Building2, Car, CalendarDays, Users, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const cards = [
  { to: "/admin/agencies", icon: Building2, label: "Agences", key: "agencies", color: "bg-[#0a1e3f]" },
  { to: "/admin/vehicles", icon: Car, label: "Véhicules", key: "vehicles", color: "bg-[#0e7a3f]" },
  { to: "/admin/bookings", icon: CalendarDays, label: "Réservations", key: "bookings", color: "bg-[#d4a437]" },
  { to: "/admin/users", icon: Users, label: "Clients", key: "clients", color: "bg-[#0a1e3f]" },
];

export default function AdminDashboard() {
  const { fmt } = useCurrency();
  const [s, setS] = useState({ agencies: 0, vehicles: 0, bookings: 0, clients: 0, revenue_xaf: 0 });
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setS(r.data)).catch(() => {});
    api.get("/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);
  const empty = s.agencies === 0 && s.vehicles === 0 && s.bookings === 0;
  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Tableau de bord</h1>
          <p className="text-sm text-gray-500 mt-1">Vue d'ensemble de votre votre plateforme.</p>
        </div>
        <Link to="/admin/agencies" data-testid="dashboard-cta-agency">
          <Button className="mbk-btn-green text-white rounded-full h-11 px-5"><Plus className="h-4 w-4 mr-1" /> Nouvelle agence</Button>
        </Link>
      </div>

      {empty && (
        <div className="mt-6 bg-gradient-to-r from-[#0a1e3f] to-[#122f5c] rounded-2xl p-6 text-white flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-[#d4a437] flex items-center justify-center flex-shrink-0"><Building2 className="h-6 w-6 text-[#0a1e3f]" /></div>
          <div className="flex-1">
            <div className="font-display text-xl font-black">Bienvenue sur votre plateforme</div>
            <p className="text-white/80 text-sm mt-1">Votre plateforme est vide. Commencez par créer votre première agence partenaire, puis ajoutez ses véhicules.</p>
          </div>
          <Link to="/admin/agencies" className="mbk-btn-gold px-4 py-2 font-semibold text-[#0a1e3f] whitespace-nowrap self-center">Commencer</Link>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link
            to={c.to}
            key={c.label}
            data-testid={`dashboard-card-${c.key}`}
            className="bg-white rounded-2xl p-5 border border-gray-100 mbk-card-shadow mbk-card-hover group"
          >
            <div className={`h-10 w-10 rounded-xl ${c.color} text-white flex items-center justify-center`}><c.icon className="h-5 w-5" /></div>
            <div className="mt-3 text-xs uppercase tracking-widest text-gray-500 font-bold">{c.label}</div>
            <div className="flex items-center justify-between mt-1">
              <div className="text-3xl font-black text-[#0a1e3f]">{s[c.key]}</div>
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-[#0a1e3f] transition-colors" />
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Revenus (total réservations)</div>
          <div className="text-4xl font-black text-[#0e7a3f] mt-2">{fmt(s.revenue_xaf)}</div>
          <p className="text-xs text-gray-500 mt-2">Total cumulé des réservations confirmées.</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Activité récente</div>
            <Link to="/admin/activity" className="text-xs text-[#0a1e3f] font-semibold hover:text-[#0e7a3f]">Voir tout →</Link>
          </div>
          <div className="mt-3 space-y-2 max-h-48 overflow-auto">
            {activity.length === 0 ? <div className="text-sm text-gray-400">Aucune activité pour l'instant.</div> :
              activity.slice(0, 6).map((a) => (
                <div key={a.id} className="text-sm flex justify-between border-b border-gray-50 pb-2">
                  <span className="text-[#0a1e3f]">{a.type} · {a.ref}</span>
                  <span className="text-xs text-gray-400">{new Date(a.at).toLocaleString("fr-FR")}</span>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}
